import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE_URL } from '../../config';
import '../AdminDashboard/BalanceEnquiry.css';

const SecondaryBalanceEnquiry = () => {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [balanceData, setBalanceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchBalanceSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBalanceSummary = async () => {
    setLoading(true);
    setError('');

    try {
      let url = `${API_BASE_URL}/api/secondary-balance-enquiry`;

      if (dateFrom && dateTo) {
        url += `?date_from=${dateFrom}&date_to=${dateTo}`;
      }

      const response = await fetch(url);
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || payload.message || 'Failed to fetch balance data');
      }

      const data = await response.json();
      setBalanceData(data);
    } catch (err) {
      console.error('Failed to fetch secondary balance enquiry data:', err);
      setError(err.message || 'Unexpected error occurred while loading balance data.');
      setBalanceData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilter = () => {
    if (dateFrom && dateTo && new Date(dateFrom) > new Date(dateTo)) {
      alert('From date cannot be greater than To date.');
      return;
    }
    fetchBalanceSummary();
  };

  const handleClearFilter = () => {
    setDateFrom('');
    setDateTo('');
    fetchBalanceSummary();
  };

  const formatCurrency = (value) => {
    const amount = Number(value || 0);
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const calculations = useMemo(() => {
    if (!balanceData) return null;

    const n = (v) => Number(v || 0);

    // Investment = PartnerTransactions + FDCollection
    const partnerTransactions = n(balanceData.secondaryPartnerCredits);
    const fdCollection = n(balanceData.secondaryFdTotalAmount);
    const investment = partnerTransactions + fdCollection;

    // Profit = TotalInterest + CDInterest + Overdue + DocumentCharges
    const generalLoanInterest = n(balanceData.secondaryGeneralLoanInterest);
    const cdInterest = n(balanceData.secondaryCdLoanInterest);
    const tbdInterest = n(balanceData.secondaryTbdLoanInterest);
    const totalInterest = generalLoanInterest + cdInterest + tbdInterest;

    const glOverdue = n(balanceData.secondaryGlOverdue);
    const cdOverdue = n(balanceData.secondaryCdOverdue);
    const tbdOverdue = n(balanceData.secondaryTbdOverdue);
    const overdue = glOverdue + cdOverdue + tbdOverdue;

    const documentCharges = n(balanceData.secondaryDocumentChargesCredit);
    const profit = totalInterest + overdue + documentCharges;

    // Expenses = Expenses + LuckyDraw
    const expenseDebit = n(balanceData.secondaryExpenseDebit);
    const luckyDraw = n(balanceData.secondaryLuckyDrawDebit);
    const expenses = expenseDebit + luckyDraw;

    // Loans Given = GeneralLoan + CDLoan + TBDLoan
    const generalLoan = n(balanceData.secondaryGeneralLoanDebit);
    const cdLoan = n(balanceData.secondaryCdLoanDebit);
    const tbdLoan = n(balanceData.secondaryTbdLoanDebit);
    const loansGiven = generalLoan + cdLoan + tbdLoan;

    // EMI Collected = GeneralLoanEMI + CDLoanEMI + TBDLoanEMI
    const generalLoanEmi = n(balanceData.secondaryGeneralLoanEmi);
    const cdLoanEmi = n(balanceData.secondaryCdLoanEmi);
    const tbdLoanEmi = n(balanceData.secondaryTbdLoanEmi);
    const emiCollected = generalLoanEmi + cdLoanEmi + tbdLoanEmi;

    // Exposure Balance = Investment + Profit − Expenses − LoansGiven
    const exposureBalance = investment + profit - expenses - loansGiven;

    // Available Cash Balance = ExposureBalance + EMICollected
    const availableCashBalance = exposureBalance + emiCollected;

    return {
      investment: {
        partnerTransactions,
        fdCollection,
        total: investment,
      },
      profit: {
        generalLoanInterest,
        cdInterest,
        tbdInterest,
        totalInterest,
        glOverdue,
        cdOverdue,
        tbdOverdue,
        overdue,
        documentCharges,
        total: profit,
      },
      expenses: {
        expenseDebit,
        luckyDraw,
        total: expenses,
      },
      loansGiven: {
        generalLoan,
        cdLoan,
        tbdLoan,
        total: loansGiven,
      },
      emiCollected: {
        generalLoanEmi,
        cdLoanEmi,
        tbdLoanEmi,
        total: emiCollected,
      },
      exposureBalance,
      availableCashBalance,
    };
  }, [balanceData]);

  const secondaryRows = useMemo(() => {
    if (!balanceData) {
      return [];
    }

    return [
      { label: 'General Loan (Debit)', value: balanceData.secondaryGeneralLoanDebit },
      { label: 'CD Loan (Debit)', value: balanceData.secondaryCdLoanDebit },
      { label: 'TBD Loan (Debit)', value: balanceData.secondaryTbdLoanDebit },
      { label: 'GeneralLoan Interest (Credit)', value: balanceData.secondaryGeneralLoanInterest },
      { label: 'CD Loan Interest (Credit)', value: balanceData.secondaryCdLoanInterest },
      { label: 'TBD Loan Interest (Credit)', value: balanceData.secondaryTbdLoanInterest },
      { label: 'GeneralLoan EMI (Credit)', value: balanceData.secondaryGeneralLoanEmi },
      { label: 'CD Loan EMI (Credit)', value: balanceData.secondaryCdLoanEmi },
      { label: 'TBD Loan EMI (Credit)', value: balanceData.secondaryTbdLoanEmi },
      { label: 'GL Overdue (Credit)', value: balanceData.secondaryGlOverdue },
      { label: 'CD Overdue (Credit)', value: balanceData.secondaryCdOverdue },
      { label: 'TBD Overdue (Credit)', value: balanceData.secondaryTbdOverdue },
      { label: 'Partner Transactions (Credit)', value: balanceData.secondaryPartnerCredits },
      { label: 'Expense (Debit)', value: balanceData.secondaryExpenseDebit },
      { label: 'Lucky Draw (Debit)', value: balanceData.secondaryLuckyDrawDebit },
      { label: 'Document charges (Credit)', value: balanceData.secondaryDocumentChargesCredit },
      { label: 'Total amount collected from the FD (Credit)', value: balanceData.secondaryFdTotalAmount },
    ];
  }, [balanceData]);

  const totalRowClass = (value) => {
    if (value > 0) return 'positive-value total-row';
    if (value < 0) return 'negative-value total-row';
    return 'total-row';
  };

  const valueClass = (value) => {
    if (value > 0) return 'positive-value';
    if (value < 0) return 'negative-value';
    return '';
  };

  const CalculationGroup = ({ title, rows, totalLabel, totalValue }) => {
    return (
      <div className="calc-group">
        <div className="calc-group-title">{title}</div>
        <table className="calc-table">
          <tbody>
            {rows.map((row) => (
              <tr key={row.label}>
                <td>{row.label}</td>
                <td className={`right ${valueClass(row.value)}`}>{formatCurrency(row.value)}</td>
              </tr>
            ))}
            <tr className="calc-total-row">
              <td>
                <strong>{totalLabel}</strong>
              </td>
              <td className={`right ${valueClass(totalValue)}`}>
                <strong>{formatCurrency(totalValue)}</strong>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="balance-enquiry-container">
      <h2>Secondary Balance Enquiry</h2>

      <div className="filter-section">
        <h3>Date Duration</h3>
        <div className="date-filters">
          <div className="filter-group">
            <label>From Date</label>
            <input
              type="date"
              className="filter-input"
              value={dateFrom}
              onChange={(event) => setDateFrom(event.target.value)}
            />
          </div>
          <div className="filter-group">
            <label>To Date</label>
            <input
              type="date"
              className="filter-input"
              value={dateTo}
              onChange={(event) => setDateTo(event.target.value)}
            />
          </div>
          <div className="filter-buttons">
            <button className="apply-button" onClick={handleApplyFilter}>
              Apply Filter
            </button>
            <button className="clear-button" onClick={handleClearFilter}>
              Clear Filter
            </button>
          </div>
        </div>
        <p className="filter-note">
          {dateFrom && dateTo
            ? `Showing data between ${dateFrom} and ${dateTo}`
            : 'Showing cumulative data (no date filter applied).'}
        </p>
      </div>

      {loading ? (
        <div className="loading-message">Loading balance data…</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : !balanceData ? (
        <div className="no-data-message">No balance data available.</div>
      ) : (
        <>
          <div className="balance-table-section">
            <h3>Business Balances (Clear Client View)</h3>
            <p className="balance-helper">
              Exposure Balance shows capital deployment (risk view). Available Cash Balance shows usable cash available today for new lending.
            </p>

            <div className="balance-card-grid">
              <div className="balance-card">
                <div className="balance-card-header">
                  <div>
                    <div className="balance-card-title">Exposure Balance</div>
                    <div className="balance-card-subtitle">Capital vs loans (risk / deployment view)</div>
                  </div>
                  <div className={`balance-card-amount ${valueClass(calculations?.exposureBalance || 0)}`}>
                    {formatCurrency(calculations?.exposureBalance || 0)}
                  </div>
                </div>

                <div className="formula-block">
                  <div className="formula-title">Formula</div>
                  <div className="formula-text">Exposure Balance = Investment + Profit − Expenses − Loans Given</div>
                  <div className="formula-text formula-sub">
                    = ({formatCurrency(calculations?.investment.total || 0)} + {formatCurrency(calculations?.profit.total || 0)}) −{' '}
                    {formatCurrency(calculations?.expenses.total || 0)} − {formatCurrency(calculations?.loansGiven.total || 0)}
                  </div>
                  <div className="formula-text formula-result">= {formatCurrency(calculations?.exposureBalance || 0)}</div>
                </div>

                <div className="calc-grid">
                  <CalculationGroup
                    title="Investment = Partner Transactions + FD Collection"
                    rows={[
                      { label: 'Partner Transactions', value: calculations?.investment.partnerTransactions || 0 },
                      { label: 'FD Collection', value: calculations?.investment.fdCollection || 0 },
                    ]}
                    totalLabel="Investment"
                    totalValue={calculations?.investment.total || 0}
                  />

                  <CalculationGroup
                    title="Profit = Total Interest + Overdue + Document Charges"
                    rows={[
                      { label: 'General Loan Interest', value: calculations?.profit.generalLoanInterest || 0 },
                      { label: 'CD Interest', value: calculations?.profit.cdInterest || 0 },
                      { label: 'TBD Interest', value: calculations?.profit.tbdInterest || 0 },
                      { label: 'Total Interest', value: calculations?.profit.totalInterest || 0 },
                      { label: 'GL Overdue', value: calculations?.profit.glOverdue || 0 },
                      { label: 'CD Overdue', value: calculations?.profit.cdOverdue || 0 },
                      { label: 'TBD Overdue', value: calculations?.profit.tbdOverdue || 0 },
                      { label: 'Overdue (Total)', value: calculations?.profit.overdue || 0 },
                      { label: 'Document Charges', value: calculations?.profit.documentCharges || 0 },
                    ]}
                    totalLabel="Profit"
                    totalValue={calculations?.profit.total || 0}
                  />

                  <CalculationGroup
                    title="Expenses = Expenses + Lucky Draw"
                    rows={[
                      { label: 'Expenses', value: calculations?.expenses.expenseDebit || 0 },
                      { label: 'Lucky Draw', value: calculations?.expenses.luckyDraw || 0 },
                    ]}
                    totalLabel="Expenses (Total)"
                    totalValue={calculations?.expenses.total || 0}
                  />

                  <CalculationGroup
                    title="Loans Given = General Loan + CD Loan + TBD Loan"
                    rows={[
                      { label: 'General Loan', value: calculations?.loansGiven.generalLoan || 0 },
                      { label: 'CD Loan', value: calculations?.loansGiven.cdLoan || 0 },
                      { label: 'TBD Loan', value: calculations?.loansGiven.tbdLoan || 0 },
                    ]}
                    totalLabel="Loans Given"
                    totalValue={calculations?.loansGiven.total || 0}
                  />
                </div>
              </div>

              <div className="balance-card">
                <div className="balance-card-header">
                  <div>
                    <div className="balance-card-title">Available Cash Balance</div>
                    <div className="balance-card-subtitle">Usable money today (available for new lending)</div>
                  </div>
                  <div className={`balance-card-amount ${valueClass(calculations?.availableCashBalance || 0)}`}>
                    {formatCurrency(calculations?.availableCashBalance || 0)}
                  </div>
                </div>

                <div className="formula-block">
                  <div className="formula-title">Formula</div>
                  <div className="formula-text">Available Cash Balance = Exposure Balance + EMI Collected</div>
                  <div className="formula-text formula-sub">
                    = {formatCurrency(calculations?.exposureBalance || 0)} + {formatCurrency(calculations?.emiCollected.total || 0)}
                  </div>
                  <div className="formula-text formula-result">= {formatCurrency(calculations?.availableCashBalance || 0)}</div>
                </div>

                <div className="calc-grid">
                  <CalculationGroup
                    title="EMI Collected = GeneralLoan EMI + CD Loan EMI + TBD Loan EMI"
                    rows={[
                      { label: 'GeneralLoan EMI', value: calculations?.emiCollected.generalLoanEmi || 0 },
                      { label: 'CD Loan EMI', value: calculations?.emiCollected.cdLoanEmi || 0 },
                      { label: 'TBD Loan EMI', value: calculations?.emiCollected.tbdLoanEmi || 0 },
                    ]}
                    totalLabel="EMI Collected"
                    totalValue={calculations?.emiCollected.total || 0}
                  />

                  <div className="calc-group calc-group-highlight">
                    <div className="calc-group-title">Direct Expanded View</div>
                    <div className="formula-text formula-sub">
                      Available Cash Balance = (Investment + Profit − Expenses) − Loans Given + EMI Collected
                    </div>
                    <div className="formula-text formula-sub">
                      = ({formatCurrency(calculations?.investment.total || 0)} + {formatCurrency(calculations?.profit.total || 0)} −{' '}
                      {formatCurrency(calculations?.expenses.total || 0)}) − {formatCurrency(calculations?.loansGiven.total || 0)} +{' '}
                      {formatCurrency(calculations?.emiCollected.total || 0)}
                    </div>
                    <div className="formula-text formula-result">
                      = {formatCurrency(calculations?.availableCashBalance || 0)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="balance-table-section">
            <h3>Reference Numbers (System Credits / Debits)</h3>
            <table className="balance-table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th className="right">Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                {secondaryRows.map((row) => (
                  <tr key={row.label}>
                    <td>{row.label}</td>
                    <td className={`right ${valueClass(row.value)}`}>{formatCurrency(row.value)}</td>
                  </tr>
                ))}

                <tr className={totalRowClass(balanceData.secondaryTotalCredit)}>
                  <td>
                    <strong>Total Credit</strong>
                  </td>
                  <td className="right">
                    <strong>{formatCurrency(balanceData.secondaryTotalCredit)}</strong>
                  </td>
                </tr>
                <tr className={totalRowClass(balanceData.secondaryTotalDebit)}>
                  <td>
                    <strong>Total Debit</strong>
                  </td>
                  <td className="right">
                    <strong>{formatCurrency(balanceData.secondaryTotalDebit)}</strong>
                  </td>
                </tr>
                <tr className={totalRowClass(balanceData.secondaryOverallBalance)}>
                  <td>
                    <strong>Legacy Overall Balance (Total Credit − Total Debit)</strong>
                  </td>
                  <td className="right">
                    <strong>{formatCurrency(balanceData.secondaryOverallBalance)}</strong>
                  </td>
                </tr>
                <tr className={totalRowClass(balanceData.secondaryFixedBalance)}>
                  <td>
                    <strong>Fixed Balance</strong>
                  </td>
                  <td className="right">
                    <strong>{formatCurrency(balanceData.secondaryFixedBalance)}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default SecondaryBalanceEnquiry;


